import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, num, StatusChip } from './common';

interface Props {
  poId: number | null;
  onClose: () => void;
  onDone: (created: any[]) => void;
}

interface LineChoice { selected: boolean; distributorId: number | null; qty: string; other: boolean }

/**
 * Re-order lines a distributor did not (fully) supply. Suggests the next ranked distributor
 * (distributor2 → distributor3, skipping ones already tried for the item); any distributor can be picked.
 */
const ReorderDialog: React.FC<Props> = ({ poId, onClose, onDone }) => {
  const [opts, setOpts] = useState<any | null>(null);
  const [choices, setChoices] = useState<Record<number, LineChoice>>({});
  const [distributors, setDistributors] = useState<Array<{ id: number; distributor_name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poId) return;
    setOpts(null); setError(null);
    Promise.all([orderProcessorAPI.reorderOptions(poId), orderProcessorAPI.distributors()])
      .then(([o, d]) => {
        setOpts(o); setDistributors(d);
        const c: Record<number, LineChoice> = {};
        o.lines.filter((l: any) => l.eligible).forEach((l: any) => {
          c[l.item_id] = {
            selected: ['not_supplied', 'partial'].includes(l.line_status),
            distributorId: l.suggested_distributor_id, qty: String(l.remaining_qty), other: false,
          };
        });
        setChoices(c);
      })
      .catch((e) => setError(errMsg(e)));
  }, [poId]);

  const distName = useMemo(() => new Map(distributors.map((d) => [d.id, d.distributor_name])), [distributors]);
  const eligible = (opts?.lines || []).filter((l: any) => l.eligible);
  const others = (opts?.lines || []).filter((l: any) => !l.eligible);
  const chosen = eligible.filter((l: any) => choices[l.item_id]?.selected);
  const invalid = chosen.filter((l: any) => !choices[l.item_id].distributorId || !(Number(choices[l.item_id].qty) > 0));
  const set = (id: number, patch: Partial<LineChoice>) => setChoices((c) => ({ ...c, [id]: { ...c[id], ...patch } }));

  const submit = async () => {
    if (!poId) return;
    setBusy(true); setError(null);
    try {
      const created = await orderProcessorAPI.reorderLines(poId, chosen.map((l: any) => ({
        item_id: l.item_id, distributor_id: Number(choices[l.item_id].distributorId), qty: Number(choices[l.item_id].qty),
      })));
      onDone(created);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!poId} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Re-order from another distributor{opts ? ` — ${opts.po.po_number}` : ''}
        <Typography variant="body2" color="text.secondary">
          The missing quantity goes on a new draft PO per distributor. The original line is marked “re-ordered” and linked to it.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!opts && !error && <CircularProgress />}
        {opts && !eligible.length && (
          <Alert severity="info">
            Nothing to re-order: only lines that are not supplied, short (partial), or still pending on a sent PO can be re-ordered.
          </Alert>
        )}
        {opts && eligible.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Medicine</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>Re-order qty</TableCell>
                  <TableCell>Already tried</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Re-order from</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eligible.map((l: any) => {
                  const c = choices[l.item_id];
                  if (!c) return null;
                  return (
                    <TableRow key={l.item_id} selected={c.selected}>
                      <TableCell padding="checkbox"><Checkbox size="small" checked={c.selected} onChange={(e) => set(l.item_id, { selected: e.target.checked })} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{l.medicine_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{l.manufacturer_name} · {l.pack_size || '—'}</Typography>
                      </TableCell>
                      <TableCell><StatusChip status={l.line_status} /></TableCell>
                      <TableCell align="right">{num(l.loose_qty, 0)}</TableCell>
                      <TableCell align="right">{num(l.received_qty, 0)}</TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={c.qty} disabled={!c.selected}
                          inputProps={{ min: 1, style: { width: 60, textAlign: 'right' } }}
                          onChange={(e) => set(l.item_id, { qty: e.target.value })} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{l.tried.map((t: any) => t.name).join(', ') || '—'}</Typography></TableCell>
                      <TableCell>
                        {c.other ? (
                          <Autocomplete size="small" options={distributors.filter((d) => d.id !== opts.po.distributor_id)}
                            getOptionLabel={(o) => o.distributor_name}
                            onChange={(_, v) => set(l.item_id, { distributorId: v ? v.id : null, other: false })}
                            renderInput={(p) => <TextField {...p} autoFocus placeholder="Search distributor" />} />
                        ) : (
                          <Select size="small" fullWidth displayEmpty disabled={!c.selected} value={c.distributorId ?? ''}
                            onChange={(e) => { const v = e.target.value as any; if (v === 'other') set(l.item_id, { other: true }); else set(l.item_id, { distributorId: Number(v) }); }}>
                            <MenuItem value="" disabled><em>{l.suggested_distributor_id ? 'Choose distributor' : 'No untried ranked distributor — pick one'}</em></MenuItem>
                            {l.ranked.filter((d: any) => d.id !== opts.po.distributor_id).map((d: any) => (
                              <MenuItem key={d.id} value={d.id}>{d.rank}. {d.name}{d.tried ? ' (already tried)' : ''}</MenuItem>
                            ))}
                            {c.distributorId && !l.ranked.some((d: any) => d.id === c.distributorId) && (
                              <MenuItem value={c.distributorId}>{distName.get(c.distributorId) || `#${c.distributorId}`}</MenuItem>
                            )}
                            <MenuItem value="other">Other distributor…</MenuItem>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {opts && others.some((l: any) => l.reordered_to_po_id) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Already re-ordered: {others.filter((l: any) => l.reordered_to_po_id).map((l: any) => l.medicine_name).join(', ')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {invalid.length > 0 && <Typography variant="caption" color="error" sx={{ mr: 'auto', ml: 1 }}>{invalid.length} selected line(s) need a distributor and a quantity</Typography>}
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={busy || !chosen.length || invalid.length > 0} onClick={submit}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}>
          Create re-order PO(s) for {chosen.length} line(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReorderDialog;
